import type { LocalInventoryFilters } from '@/types';
import { isNumericConditionActive } from './apply-numeric-condition';

/**
 * Number of ACTIVE inventory sidebar filters — the single source for the
 * funnel-icon badge and the "Clear All" visibility in FilterSidebar.
 *
 * Every filter field must be counted here: a missing term makes the sidebar
 * claim "no filters" while the table is filtered — exactly the Exchange-only
 * regression LR-518 r1 M-2 fixed (and r2 #4 pinned with the unit test).
 * The L2CP condition group counts as ONE filter, matching its single sidebar
 * section; the ex-date range counts as one whichever bound is set.
 */
export function countActiveInventoryFilters(f: LocalInventoryFilters): number {
  const l2cpActive =
    (f.l2cpCounterparties?.length ?? 0) > 0 ||
    isNumericConditionActive(f.l2cpQuantityCondition) ||
    isNumericConditionActive(f.l2cpAmountEurCondition) ||
    isNumericConditionActive(f.l2cpRateCondition) ||
    isNumericConditionActive(f.l2cpGrossRateCondition) ||
    isNumericConditionActive(f.l2cpSnpDeltaCondition);

  return (
    (f.accountIds.length > 0 ? 1 : 0) +
    (f.counterparties.length > 0 ? 1 : 0) +
    (f.currencies.length > 0 ? 1 : 0) +
    (f.exchanges.length > 0 ? 1 : 0) +
    (f.caStatuses.length > 0 ? 1 : 0) +
    (f.caTypes.length > 0 ? 1 : 0) +
    (f.brokerAccountNames.length > 0 ? 1 : 0) +
    (f.brokerSettlementCounterparties.length > 0 ? 1 : 0) +
    (isNumericConditionActive(f.quantityCondition) ? 1 : 0) +
    (isNumericConditionActive(f.settledQuantityCondition) ? 1 : 0) +
    (isNumericConditionActive(f.quantityT1Condition) ? 1 : 0) +
    (isNumericConditionActive(f.quantityT2Condition) ? 1 : 0) +
    (isNumericConditionActive(f.quantityTxCondition) ? 1 : 0) +
    (isNumericConditionActive(f.leverageRateCondition) ? 1 : 0) +
    (isNumericConditionActive(f.valueEURCondition) ? 1 : 0) +
    (isNumericConditionActive(f.currencyBenchmarkCondition) ? 1 : 0) +
    (isNumericConditionActive(f.dailyPotentialCondition) ? 1 : 0) +
    (isNumericConditionActive(f.adtv30DCondition) ? 1 : 0) +
    (isNumericConditionActive(f.adtvMultipleCondition) ? 1 : 0) +
    (isNumericConditionActive(f.adtvAdjustedCondition) ? 1 : 0) +
    (isNumericConditionActive(f.benchmarkFeeCondition) ? 1 : 0) +
    (isNumericConditionActive(f.activeUtilisationCondition) ? 1 : 0) +
    (isNumericConditionActive(f.quantityOnLoanCondition) ? 1 : 0) +
    (isNumericConditionActive(f.rebateRateCondition) ? 1 : 0) +
    (isNumericConditionActive(f.pendingTransfersOutCondition) ? 1 : 0) +
    (isNumericConditionActive(f.cimbEq2QuantityCondition) ? 1 : 0) +
    (isNumericConditionActive(f.cimbSfQuantityCondition) ? 1 : 0) +
    (isNumericConditionActive(f.cimbSf3QuantityCondition) ? 1 : 0) +
    (isNumericConditionActive(f.velocityLQuantityCondition) ? 1 : 0) +
    (isNumericConditionActive(f.velocityMQuantityCondition) ? 1 : 0) +
    (l2cpActive ? 1 : 0) +
    (f.exDateFrom || f.exDateTo ? 1 : 0)
  );
}
